import { alpha, Box, Stack, Typography } from '@mui/material';
import { useEffect, useRef } from 'react';
import { useBetslipContext } from '../../../../contexts/BetslipContext';
import { useSlipBonusCampaigns } from '../../../../queries';
import useLocalization from '../../../../hooks/useLocalization';
import { BETSLIP_TICKET_TYPE } from '../../../../constants';
import { NUM_OF_SELECTIONS_BONUS_ELIGIBILITY, WALLET_TYPE, type WalletType } from '../../../../types/enums';

type NeobetBonusTrackerProps = {
  selectedWallet: WalletType;
  isSystemTicket: boolean;
};

const styles = {
  root: {
    p: 0.75,
    borderRadius: 1.5,
    border: '1px solid',
    borderColor: 'neutral.500',
    backgroundColor: 'neutral.700',
    display: 'grid',
    gap: 0.875,
  },
  topRow: {
    justifyContent: 'center',
    alignItems: 'center',
    gap: 1,
  },
  message: {
    typography: 'body4',
    color: 'neutral.300',
    fontWeight: 500,
  },
  stepsViewport: {
    overflowX: 'auto',
    overflowY: 'hidden',
    pb: 0.25,
    msOverflowStyle: 'none',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  stepsRow: {
    display: 'flex',
    gap: 0.375,
    width: 'max-content',
    minWidth: '100%',
  },
  step: {
    display: 'grid',
    gap: 0.375,
    justifyItems: 'center',
    flex: '0 0 36px',
  },
  stepLabel: (active: boolean, visible: boolean) => ({
    minHeight: 14,
    px: 0.5,
    py: 0.125,
    borderRadius: 1,
    typography: 'caption',
    fontWeight: 800,
    color: visible ? (active ? 'neutral.25' : 'neutral.300') : 'transparent',
    backgroundColor: 'transparent',
  }),
  stepBar: (active: boolean) => ({
    width: '100%',
    height: 8,
    borderRadius: 0.5,
    backgroundColor: active ? 'success.main' : alpha('#ffffff', 0.2),
  }),
} as const;

const ticketTypeMap = {
  [BETSLIP_TICKET_TYPE.PRE_MATCH]: NUM_OF_SELECTIONS_BONUS_ELIGIBILITY.PRE_MATCH,
  [BETSLIP_TICKET_TYPE.IN_PLAY]: NUM_OF_SELECTIONS_BONUS_ELIGIBILITY.IN_PLAY,
  [BETSLIP_TICKET_TYPE.MIXED]: NUM_OF_SELECTIONS_BONUS_ELIGIBILITY.MIXED,
} as const;

const formatMultiplier = (percentage: number) => {
  const value = (1 + percentage / 100).toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
  return `x${value}`;
};

const NeobetBonusTracker = ({ selectedWallet, isSystemTicket }: NeobetBonusTrackerProps) => {
  const { t } = useLocalization();
  const { betslipTicketType, bets } = useBetslipContext();
  const { data: bonusCampaigns } = useSlipBonusCampaigns();
  const stepRefs = useRef<Array<HTMLDivElement | null>>([]);
  const resolvedWallet = selectedWallet || WALLET_TYPE.MAIN;

  const shouldHide = resolvedWallet === WALLET_TYPE.BONUS || isSystemTicket || !bonusCampaigns;

  const campaign = shouldHide ? null : (bonusCampaigns[ticketTypeMap[betslipTicketType]]?.[0] ?? null);

  const minRequiredOdd = campaign?.minReqOdd ?? 1;
  const eligibleSelections = campaign ? bets.filter((bet) => Number.parseFloat(bet.odds) >= minRequiredOdd).length : 0;

  const steps = campaign
    ? Object.entries(campaign.bonusPercentagePerSelection)
        .map(([selectionCount, percentage]) => ({
          selectionCount: Number(selectionCount),
          percentage,
          multiplier: formatMultiplier(percentage),
        }))
        .sort((a, b) => a.selectionCount - b.selectionCount)
    : [];

  const lastStepCount = steps[steps.length - 1]?.selectionCount ?? 0;
  const stepBySelectionCount = new Map(steps.map((step) => [step.selectionCount, step]));

  const progressSteps = Array.from({ length: lastStepCount }, (_, index) => {
    const selectionCount = index + 1;
    const milestone = stepBySelectionCount.get(selectionCount);

    return {
      selectionCount,
      percentage: milestone?.percentage ?? null,
      multiplier: milestone?.multiplier ?? '',
    };
  });

  const currentStep = [...steps].reverse().find((step) => eligibleSelections >= step.selectionCount) ?? null;
  const nextStep = steps.find((step) => eligibleSelections < step.selectionCount && step.percentage !== null) ?? null;
  const selectionsNeeded = nextStep ? Math.max(nextStep.selectionCount - eligibleSelections, 0) : 0;

  const message = nextStep
    ? t('bonusInfo.addMoreSelections', {
        count: selectionsNeeded,
        odd: minRequiredOdd.toFixed(2),
        percentage: nextStep.percentage,
      })
    : t('bonusInfo.maxBonusReached', 'Max bonus reached!');

  const focusStepCount = nextStep?.selectionCount ?? currentStep?.selectionCount ?? null;

  useEffect(() => {
    if (!focusStepCount) return;

    stepRefs.current[focusStepCount - 1]?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }, [focusStepCount]);

  if (shouldHide || !campaign || steps.length === 0) {
    return null;
  }

  return (
    <Box sx={styles.root} data-testid='neobet-bonus-tracker'>
      <Stack direction='row' sx={styles.topRow}>
        {message ? <Typography sx={styles.message}>{message}</Typography> : <Box />}
      </Stack>

      <Box sx={styles.stepsViewport} data-testid='neobet-bonus-steps-viewport'>
        <Box sx={styles.stepsRow}>
          {progressSteps.map((step) => {
            const active = eligibleSelections >= step.selectionCount;
            const isMilestone = Boolean(step.percentage);

            return (
              <Box
                key={step.selectionCount}
                ref={(element: HTMLDivElement | null) => {
                  stepRefs.current[step.selectionCount - 1] = element;
                }}
                sx={styles.step}
                data-testid={`neobet-bonus-step-${step.selectionCount}`}
              >
                <Box sx={styles.stepLabel(active, isMilestone)}>{step.multiplier || 'x'}</Box>
                <Box sx={styles.stepBar(active)} data-testid={`neobet-bonus-step-bar-${step.selectionCount}`} />
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default NeobetBonusTracker;
